import { getLocalDb } from '../client'
import { STORES } from '../constants'
import { catalogVersion } from '../catalogVersion'

/**
 * Repositori produk katalog lokal. Objek produk dari BE `/pos/products/changed`
 * disimpan utuh di IndexedDB; query grid/cari dilakukan di memori (IndexedDB tak
 * punya LIKE) memakai cache yang di-reload saat versi katalog berubah.
 */

const toInt = (v) => {
  if (v == null || v === '') {
    return null
  }
  const n = Number(v)
  return Number.isFinite(n) ? Math.trunc(n) : null
}

/** id produk dari beberapa kemungkinan nama field BE. */
export const getProductRowId = (raw) => toInt(raw?.id ?? raw?.finance_account_id ?? raw?.product_id)

export const getProductCategoryId = (raw) =>
  toInt(
    raw?.category_id ??
      raw?.pos_product_category_id ??
      raw?.product_category_id ??
      raw?.finance_product_category_id,
  )

/**
 * Normalisasi produk dari payload `/pos/products/changed`:
 * - `wholesale_price` datang sebagai STRING JSON (mis. `"[{\"price\":200000,\"min_qty\":2}]"`).
 *   Konsumen harga memanggil .forEach/.find → string bikin crash; bila dibuang, tier
 *   grosir hilang diam-diam. Parse ke array; gagal/non-array → [].
 */
export const parseProductRow = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return raw
  }
  const product = { ...raw }
  if (typeof product.wholesale_price === 'string') {
    try {
      const parsed = JSON.parse(product.wholesale_price)
      product.wholesale_price = Array.isArray(parsed) ? parsed : []
    } catch {
      product.wholesale_price = []
    }
  }
  return product
}

const toFlag = (v, fallback) => {
  if (v == null || v === '') {
    return fallback
  }
  if (typeof v === 'boolean') {
    return v ? 1 : 0
  }
  return Number(v) ? 1 : 0
}

// Produk arsip/nonaktif ikut dikirim delta `/pos/products/changed` (agar perubahan
// statusnya tercatat) tapi tidak boleh tampil di grid, pencarian, maupun scan barcode.
const isListable = (product) => !toFlag(product?.is_archive, 0) && !!toFlag(product?.is_active, 1)

let cache = null
let cacheVersion = -1
// Muat bersamaan (grid + detail + barcode) cukup satu kali baca IndexedDB per versi.
let loading = null
let loadingVersion = -1
const nameCollator = new Intl.Collator(undefined, { sensitivity: 'base' })

const buildCache = async () => {
  const db = await getLocalDb()
  const rows = await db.getAll(STORES.PRODUCTS)
  const all = rows.map(parseProductRow)
  const byId = new Map()
  all.forEach((product) => byId.set(getProductRowId(product), product))
  const list = all.filter(isListable)
  list.sort((a, b) => nameCollator.compare(String(a?.name ?? ''), String(b?.name ?? '')))
  const byCode = new Map()
  list.forEach((product) => {
    if (product?.barcode) {
      byCode.set(String(product.barcode), product)
    }
    const code = product?.code ?? product?.sku
    if (code && !byCode.has(String(code))) {
      byCode.set(String(code), product)
    }
  })
  return { list, byId, byCode }
}

const loadCache = async () => {
  const version = catalogVersion.getSnapshot()
  if (cache && cacheVersion === version) {
    return cache
  }
  if (!loading || loadingVersion !== version) {
    loadingVersion = version
    loading = buildCache()
      .then((built) => {
        if (loadingVersion === version) {
          cache = built
          cacheVersion = version
        }
        return built
      })
      .finally(() => {
        if (loadingVersion === version) {
          loading = null
        }
      })
  }
  return loading
}

/** Buang cache memori (dipanggil setelah data produk berubah). */
export const invalidateProductCache = () => {
  cache = null
  // Muatan yang sedang berjalan bisa membawa data lama → jangan disimpan.
  loading = null
  loadingVersion = -1
}

/** Upsert batch produk. Baris tanpa id valid dilewati. */
export const upsertProducts = async (list) => {
  if (!Array.isArray(list) || !list.length) {
    return
  }
  const db = await getLocalDb()
  const tx = db.transaction(STORES.PRODUCTS, 'readwrite')
  list.forEach((raw) => {
    const id = getProductRowId(raw)
    if (id != null) {
      tx.store.put({ ...raw, id })
    }
  })
  await tx.done
  invalidateProductCache()
}

/** Hapus produk berdasarkan daftar id (dari `deleted[]` respons changed). */
export const deleteProductsByIds = async (ids) => {
  const clean = (ids || []).map(toInt).filter((v) => v != null)
  if (!clean.length) {
    return
  }
  const db = await getLocalDb()
  const tx = db.transaction(STORES.PRODUCTS, 'readwrite')
  clean.forEach((id) => tx.store.delete(id))
  await tx.done
  invalidateProductCache()
}

export const countProducts = async () => {
  const db = await getLocalDb()
  return db.count(STORES.PRODUCTS)
}

const matchesSearch = (product, query) => {
  if (!query) {
    return true
  }
  return [product?.name, product?.code, product?.sku, product?.barcode].some(
    (value) => value != null && String(value).toLowerCase().includes(query),
  )
}

/**
 * Query grid produk — mereplikasi param grid kasir (cat_ids, is_favorite, search,
 * product_ids, exclude_product_ids, variant_type_ids, hide_serial_product, is_sell).
 * Kembalikan `{ rows, total }`.
 */
export const queryProducts = async ({
  search,
  catIds = [],
  productIds = [],
  excludeProductIds = [],
  variantTypeIds = [],
  isFavorite = false,
  isSell = true,
  hideSerial = false,
  limit = 30,
  offset = 0,
}) => {
  const { list } = await loadCache()
  const query = String(search || '')
    .trim()
    .toLowerCase()
  const catSet = new Set(catIds)
  const productSet = new Set(productIds)
  const excludeSet = new Set(excludeProductIds)
  const variantSet = new Set(variantTypeIds)

  const filtered = list.filter((product) => {
    const id = getProductRowId(product)
    if (isSell && !toFlag(product?.is_sell ?? product?.is_pos, 1)) {
      return false
    }
    if (hideSerial && toFlag(product?.is_serial ?? product?.is_track_serial_number, 0)) {
      return false
    }
    if (isFavorite && !toFlag(product?.is_favorite, 0)) {
      return false
    }
    if (catSet.size && !catSet.has(getProductCategoryId(product))) {
      return false
    }
    if (variantSet.size) {
      const variant = toInt(product?.variant_type_id ?? product?.product_variant_type_id) ?? 0
      if (!variantSet.has(variant)) {
        return false
      }
    }
    if (productSet.size && !productSet.has(id)) {
      return false
    }
    if (excludeSet.size && excludeSet.has(id)) {
      return false
    }
    return matchesSearch(product, query)
  })

  return { rows: filtered.slice(offset, offset + limit), total: filtered.length }
}

export const getProductById = async (id) => {
  const { byId } = await loadCache()
  return byId.get(toInt(id)) ?? null
}

/** Cari produk berdasarkan barcode ATAU kode/SKU (BE `changed` tak selalu kirim barcode). */
export const findProductByBarcode = async (barcode) => {
  if (!barcode) {
    return null
  }
  const { byCode } = await loadCache()
  return byCode.get(String(barcode).trim()) ?? null
}
